// ParentDashboard (API-backed, Date of Birth)
import React, { useState, useEffect } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { studentService } from '../../services/studentService';
import { reportService } from '../../services/reportService';
import { subscriptionService } from '../../services/subscriptionService';
import { useAuth } from '../../context/AuthContext';
import { User, TrendingUp, Clock, Target, Award, Calendar, DollarSign, Users, BarChart3, Activity, CheckCircle, Edit2, Trash2, X, Plus } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import LoadingSpinner from '../shared/LoadingSpinner';
import Navbar from '../shared/Navbar';
import googlePlayBadge from '../../assets/images/google-play-badge.png';

const PRICING_BY_REGION = {
    EU: {
        currency: 'EUR',
        symbol: '€',
        single: {
            monthly: { amount: 9.99, price_id: 'price_1T5qDaEmipxW1R9kLwgud0cQ' },
            yearly:  { amount: 6.99, price_id: 'price_1T5qDaEmipxW1R9kd1RMeehK' }
        },
        family: {
            monthly: { amount: 24.99, price_id: 'price_1T5qDZEmipxW1R9kTk04ckk3' },
            yearly:  { amount: 15.99, price_id: 'price_1T5qDZEmipxW1R9k7rSLk96L' }
        }
    },

    UK: {
        currency: 'GBP',
        symbol: '£',
        single: {
            monthly: { amount: 10.99, price_id: 'price_1T5qDZEmipxW1R9kteJZsCwa' },
            yearly:  { amount: 7.99,  price_id: 'price_1T5qDZEmipxW1R9k6yoPuNbV' }
        },
        family: {
            monthly: { amount: 25.99, price_id: 'price_1T5qDZEmipxW1R9k7A95MXRi' },
            yearly:  { amount: 16.99, price_id: 'price_1T5qDZEmipxW1R9kClXrYHCl' }
        }
    },

    USA: {
        currency: 'USD',
        symbol: '$',
        single: {
            monthly: { amount: 14.99, price_id: 'price_1T5qDZEmipxW1R9kuh6DbKZt' },
            yearly:  { amount: 9.99,  price_id: 'price_1T5qDZEmipxW1R9kNKbWTacI' }
        },
        family: {
            monthly: { amount: 39.99, price_id: 'price_1T5qDZEmipxW1R9k1TZ3BPhW' },
            yearly:  { amount: 24.99, price_id: 'price_1T5qDZEmipxW1R9kXVMFdAna' }
        }
    },

    NG: {
        currency: 'NGN',
        symbol: '₦',
        single: {
            monthly: { amount: 7999.00,  price_id: 'price_1T5qDZEmipxW1R9koHlsaNUq' },
            yearly:  { amount: 5999.00,  price_id: 'price_1T5qDZEmipxW1R9kBRy3Uauo' }
        },
        family: {
            monthly: { amount: 18999.00, price_id: 'price_1T5qDZEmipxW1R9kuKkabKh6' },
            yearly:  { amount: 15999.00, price_id: 'price_1T5qDYEmipxW1R9kxgCj4Xwh' }
        }
    },

    MY: {
        currency: 'MYR',
        symbol: 'RM',
        single: {
            monthly: { amount: 23.99, price_id: 'price_1T5qDYEmipxW1R9kGa8bPqT2' },
            yearly:  { amount: 15.56, price_id: 'price_1T5qDYEmipxW1R9kvxL5a0zM' }
        },
        family: {
            monthly: { amount: 62.99, price_id: 'price_1T5qDYEmipxW1R9kt6Z9eYy0' },
            yearly:  { amount: 54.99, price_id: 'price_1T5qDYEmipxW1R9kevcN9Rmb' }
        }
    },

    OTHER: {
        currency: 'USD',
        symbol: '$',
        single: {
            monthly: { amount: 6.99, price_id: 'price_1T5qDYEmipxW1R9kBmG1PmXv' },
            yearly:  { amount: 4.99, price_id: 'price_1T5qDYEmipxW1R9kIOKjZIxB' }
        },
        family: {
            monthly: { amount: 16.99, price_id: 'price_1T5qDYEmipxW1R9kNJyG72I6' },
            yearly:  { amount: 13.99, price_id: 'price_1T5qDYEmipxW1R9kA4tg1sjp' }
        }
    }
};

const EURO_COUNTRIES = [
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR',
    'HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK',
    'SI','ES','SE'
];

const getRegionByCountry = (code) => {
    if (code === 'GB') return 'UK';
    if (code === 'US') return 'USA';
    if (code === 'NG') return 'NG';
    if (code === 'MY') return 'MY';
    if (EURO_COUNTRIES.includes(code)) return 'EU';
    return 'OTHER';
};

const HOBBIES = [
    { id: 'building', label: 'Building & Engineering' },
    { id: 'experiments', label: 'Science Experiments' },
    { id: 'math-logic', label: 'Math & Logic' },
];

const ParentDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth(); // get logged-in user
    const [students, setStudents] = useState([]);
    const [selectedStudentIdx, setSelectedStudentIdx] = useState(0);
    const [overview, setOverview] = useState(null);
    const [progress, setProgress] = useState([]);
    const [activity, setActivity] = useState([]);
    const [badges, setBadges] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    /* Pricing Modal State */
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [billing, setBilling] = useState('yearly');
    const [region, setRegion] = useState('USA');

    // Modal & form state
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [formData, setFormData] = useState({ full_name: '', dob: '', avatar: '👨‍🎓', level_id: null, hobbies: [] });

    const avatars = ['🦸‍♂️', '🦸‍♀️', '🧑‍🎓', '👨‍🎓', '👩‍🎓'];

    const pricing = PRICING_BY_REGION[getRegionByCountry(user.country_code)];

    const getMaxProfilesFromSubscription = (priceId) => {
        if (!priceId) return 1;

        return Object.values(PRICING_BY_REGION).some(region =>
            Object.values(region.family).some(plan => plan.price_id === priceId)
        )
            ? 3
            : 1;
    };

    const toggleHobby = (id) => {
        setFormData(prev => {
            if (prev.hobbies.includes(id)) {
                return { ...prev, hobbies: prev.hobbies.filter(h => h !== id) };
            }
            if (prev.hobbies.length >= 3) return prev;
            return { ...prev, hobbies: [...prev.hobbies, id] };
        });
    };

    useEffect(() => {
        fetchParentData();
    }, []);

    useEffect(() => {
        if (students.length > 0) {
            const idx = Math.min(selectedStudentIdx, students.length - 1);
            setSelectedStudentIdx(idx);
            fetchStudentReports(students[idx].id);
        } else {
            setOverview(null);
            // setProgress([]);
            // setActivity([]);
            // setBadges([]);
        }
    }, [students]);

    useEffect(() => {
        if (students.length > 0) {
            const student = students[selectedStudentIdx];
            if (student) fetchStudentReports(student.id);
        }
    }, [selectedStudentIdx]);

    const fetchParentData = async () => {
        try {
            setLoading(true);
            const [studentsData, subData] = await Promise.all([
                studentService.getStudents(),
                subscriptionService.getSubscriptionStatus()
            ]);

            const normalizedStudents = (studentsData.students || []).map(s => ({
                ...s,
                hobbies: Array.isArray(s.hobbies)
                    ? s.hobbies
                    : typeof s.hobbies === "string"
                        ? JSON.parse(s.hobbies)
                        : []
            }));

            setStudents(normalizedStudents);
            setSubscription(subData.subscription || null);

            if (!subData.subscription) {
                setShowPricingModal(true);
            }
            if (subData.subscription && !subData.hasActiveSubscription) {
                handleManageSubscription();
            }
        } catch (err) {
            console.error('Failed to fetch parent data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentReports = async (studentId) => {
        try {
            const [overviewData, progressData, activityData, badgesData] = await Promise.all([
                reportService.getStudentOverview(studentId),
                // reportService.getStudentProgress(studentId),
                // reportService.getStudentActivity(studentId),
                // reportService.getStudentBadges(studentId)
            ]);

            setOverview(overviewData.overview || null);
            // setProgress(progressData.progress || []);
            // setActivity(activityData.activity || []);
            // setBadges(badgesData.badges || []);
        } catch (err) {
            console.error('Failed to fetch student reports:', err);
        }
    };

    const maxProfilesAllowed = getMaxProfilesFromSubscription(subscription?.stripe_price_id);

    const openAddModal = () => {
        setEditingStudent(null);
        setFormData({ full_name: '', dob: '', avatar: '👨‍🎓', level_id: null, hobbies: HOBBIES.map(h => h.id) });
        setShowAddModal(true);
    };

    const openEditModal = (student) => {
        setEditingStudent(student);
        setFormData({
            full_name: student.full_name,
            dob: student.date_of_birth
                ? new Date(student.date_of_birth).toISOString().split('T')[0]
                : '',
            avatar: student.avatar || '👨‍🎓',
            level_id: student.level_id || null,
            hobbies: student.hobbies || []
        });
        setShowAddModal(true);
    };

    const handleDeleteStudent = async (studentId) => {
        if (!window.confirm('Are you sure you want to remove this child? All their progress will be deleted.')) return;
        try {
            setLoading(true);
            await studentService.deleteStudent(studentId);
            await fetchParentData();
            setShowAddModal(false);
        } catch (err) {
            console.error('Failed to delete student:', err);
            alert('Failed to delete student. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const calculateAge = (dob) => {
        if (!dob) return 0;

        const birthDate = new Date(dob);
        const today = new Date();

        let age = today.getFullYear() - birthDate.getFullYear();

        // Check if birthday has not occurred yet this year
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!formData.full_name || !formData.dob) {
            alert('Please fill in name and date of birth');
            return;
        }

        try {
            setLoading(true);
            if (editingStudent) {
                await studentService.updateStudent(editingStudent.id, {
                    full_name: formData.full_name,
                    dob: formData.dob,
                    avatar: formData.avatar,
                    level_id: formData.level_id,
                    hobbies: formData.hobbies
                });
            } else {
                await studentService.createStudent({
                    full_name: formData.full_name,
                    dob: formData.dob,
                    avatar: formData.avatar,
                    level_id: formData.level_id,
                    parent_id: user.id,
                    hobbies: formData.hobbies
                });
            }
            await fetchParentData();
            setShowAddModal(false);
            setEditingStudent(null);
            setFormData({ full_name: '', dob: '', avatar: '👨‍🎓', level_id: null, hobbies: [] });
        } catch (err) {
            alert('Failed to save student. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const today = new Date();

    const maxDob = new Date(
        today.getFullYear() - 5,
        today.getMonth(),
        today.getDate()
    );

    const minDob = new Date(
        today.getFullYear() - 7,
        today.getMonth(),
        today.getDate()
    );

    const formatDate = (date) => date.toISOString().split('T')[0];

    const selectStudentByIndex = (idx) => setSelectedStudentIdx(idx);

    const selectedStudent = students[selectedStudentIdx] || null;
    const COLORS = ['#8b5cf6', '#ec4899', '#3b82f6', '#10b981'];

    const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4" style={{ borderColor: color }}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-500 text-sm font-medium">{label}</p>
                    <p className="text-3xl font-bold mt-2" style={{ color }}>{value}</p>
                    {subtext && <p className="text-gray-400 text-xs mt-1">{subtext}</p>}
                </div>
                <Icon className="w-12 h-12 opacity-20" style={{ color }} />
            </div>
        </div>
    );

    const handleSubscribe = async (price_id) => {
        try {
            const res = await subscriptionService.createSubscription(price_id);

            if (res?.url) {
                window.location.href = res.url; // Redirect to Stripe Checkout
            }
        } catch (err) {
            console.error('Subscription error:', err);
            alert('Unable to start subscription.');
        }
    };

    const handleManageSubscription = async () => {
        try {
            const res = await subscriptionService.createPortalSession();

            if (res?.url) {
                window.location.href = res.url;
            }
        } catch (err) {
            alert("Unable to open subscription management.");
        }
    };

    if (loading) return <LoadingSpinner message="Loading parent dashboard..." />;

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-xl p-6 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold">Parent Dashboard</h1>
                            <p className="mt-2 opacity-90">Monitor your children's learning progress</p>
                        </div>
                        {subscription && (
                            <div className="bg-white bg-opacity-20 rounded-xl p-4 backdrop-blur-sm">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle className="w-5 h-5" />
                                    <span className="font-semibold">{subscription.plan_name}</span>
                                </div>
                                <p className="text-sm opacity-90">{students.length}/{maxProfilesAllowed} profiles</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Student Selector */}
                <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                    <div className="flex items-center gap-4 overflow-x-auto">
                        {students.map((student, idx) => (
                            <div key={student.id} className="relative flex-shrink-0 group">
                                <button
                                    onClick={() => selectStudentByIndex(idx)}
                                    className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${
                                        selectedStudent?.id === student.id
                                            ? 'bg-purple-100 border-2 border-purple-500'
                                            : 'bg-gray-50 hover:bg-gray-100'
                                    }`}
                                >
                                    <span className="text-3xl">{student.avatar || '👨‍🎓'}</span>
                                    <div className="text-left">
                                        <p className="font-semibold">{student.full_name}</p>
                                        <p className="text-xs text-gray-500">{student.date_of_birth ? calculateAge(student.date_of_birth) +'yrs' : '—'} • {student.level_name || ''}</p>
                                        <p className="text-xs text-gray-500">User Id: {student.email?.split('@')[0] ?? ''} • Passcode: {student.password_hash}</p>
                                    </div>
                                    {selectedStudent?.id === student.id && <CheckCircle className="w-5 h-5 text-purple-600" />}
                                </button>

                                <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); openEditModal(student); }}
                                        className="bg-blue-500 text-white p-1.5 rounded-full hover:bg-blue-600 shadow-lg"
                                    >
                                        <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteStudent(student.id); }}
                                        className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-lg"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}

                        {subscription && students.length < maxProfilesAllowed && (
                            <button
                                onClick={openAddModal}
                                className="flex items-center gap-2 px-6 py-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 transition-all flex-shrink-0"
                            >
                                <Plus className="w-5 h-5 text-gray-400" />
                                <span className="text-gray-600 font-medium">Add Child</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Rest of dashboard tabs & content (overview, activity, performance, badges) remain unchanged */}
                <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">

                    <div className="p-6">
                        {/* Overview Tab */}
                        {activeTab === 'overview' && overview && (
                            <div className="space-y-6">
                                {/* Key Stats */}
                                <div className="grid md:grid-cols-4 gap-4">
                                    <StatCard
                                        icon={Target}
                                        label="Progress"
                                        value={
                                            overview.total_lessons
                                                ? `${Math.round((overview.lessons_completed / overview.total_lessons) * 100)}%`
                                                : '0%'
                                        }
                                        subtext={`${overview.lessons_completed || 0}/${overview.total_lessons || 0} lessons`}
                                        color="#8b5cf6"
                                    />
                                    <StatCard
                                        icon={TrendingUp}
                                        label="Average Score"
                                        value={`${overview.average_score}%`}
                                        subtext="Across all experiments"
                                        color="#ec4899"
                                    />
                                    <StatCard
                                        icon={Clock}
                                        label="Time Spent"
                                        value={`-`}
                                        // value={`${Math.floor(overview.total_time_minutes / 60)}h ${overview.total_time_minutes % 60}m`}
                                        subtext="Total learning time"
                                        color="#3b82f6"
                                    />
                                    <StatCard
                                        icon={Award}
                                        label="Badges Earned"
                                        value={overview.badges_earned}
                                        subtext={`${overview.streak_days || 0} day streak 🔥`}
                                        color="#10b981"
                                    />
                                </div>

                                {/* Level Progress */}
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-bold text-gray-800">Current Level: {overview.current_level ?? 'Not Started'}</h3>
                                        <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          {Math.round((overview.lessons_completed / overview.total_lessons) * 100) ?? 0}% Complete
                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                                        <div
                                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-4 rounded-full transition-all"
                                            style={{ width: `${Math.round((overview.lessons_completed / overview.total_lessons) * 100) ?? 0}%` }}
                                        />
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {(overview.total_lessons ?? 0) - (overview.lessons_completed ?? 0)} lessons remaining to complete this level
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid md:grid-cols-3 gap-6">
                    <button onClick={handleManageSubscription}
                        className="bg-gray-100 rounded-xl shadow-md p-6 text-left opacity-100"
                    >
                        <DollarSign className="w-8 h-8  mb-3" />
                        <h3 className="font-semibold text-lg mb-2 ">
                            Manage Subscription
                        </h3>
                        <p className="text-sm ">
                            Update payment or cancel subscription
                        </p>
                    </button>

                    <dive className="rounded-xl shadow-md p-6 text-left opacity-100">
                        <p className="text-sm ">
                            Download STEM4Kiddo App on your Child's device.
                        </p>
                        <a href="https://play.google.com/store/apps/details?id=pt.fancybox.stem4kiddo" target="_blank"><img src={googlePlayBadge} alt="Download on Google Play Store"/> </a>
                    </dive>

                </div>

                {/* -------------------- PRICING MODAL -------------------- */}
                {showPricingModal && (
                    // <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    //     <div className="bg-white rounded-s-2xl max-w-5xl w-full p-2 relative">
                    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto">
                        <div className="flex min-h-screen items-center justify-center p-4">
                            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-4 relative">

                                <button
                                    onClick={() => subscription && setShowPricingModal(false)}
                                    className="absolute top-4 right-4 text-gray-500"
                                >
                                    <X />
                                </button>

                                <div className="max-w-5xl mx-auto px-4 text-center">
                                    <h2 className="text-4xl font-bold mb-4">Our Pricing</h2>
                                    <p className="text-xl text-gray-600 mb-8">
                                        Choose the plan that fits your family. Cancel anytime.
                                    </p>

                                    {/* Billing Toggle */}
                                    <div className="flex justify-center mb-8">
                                        <div className="bg-gray-100 rounded-full p-1 flex">
                                            {['monthly','yearly'].map(type => (
                                                <button
                                                    key={type}
                                                    onClick={() => setBilling(type)}
                                                    className={`px-6 py-2 rounded-full ${
                                                        billing === type ? 'bg-white shadow text-purple-600' : ''
                                                    }`}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-8">

                                        {/* SINGLE */}
                                        <div className="border rounded-2xl p-8">
                                            <h3 className="text-2xl font-bold mb-2">Individual Plan</h3>
                                            <p className="text-gray-600 mb-6">Perfect for one learner</p>

                                            <p className="text-4xl font-bold">
                                                {pricing.symbol}{Number(pricing.single[billing].amount).toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                                <span className="text-lg text-gray-500"> /month</span>
                                            </p>

                                            {billing === 'yearly' && (
                                                <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                                            )}

                                            <ul className="space-y-3 my-8 text-left">
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> 1 child profile</li>
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Personalized learning path</li>
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Structured STEM lessons</li>
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> 7-day free trial</li>
                                            </ul>

                                            <button
                                                onClick={() => handleSubscribe(pricing.single[billing].price_id)}
                                                className="block w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition"
                                            >
                                                Start Free Trial
                                            </button>
                                        </div>

                                        {/* FAMILY */}
                                        <div className="border-2 border-purple-600 bg-purple-50 rounded-2xl p-8 relative">
                                            <span className="absolute top-4 right-4 bg-purple-600 text-white text-xs px-3 py-1 rounded-full">
                                                Most Popular
                                            </span>

                                            <h3 className="text-2xl font-bold mb-2">Family Plan</h3>
                                            <p className="text-gray-600 mb-6">Best value for families</p>

                                            <p className="text-4xl font-bold">
                                                {pricing.symbol}{Number(pricing.family[billing].amount).toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                                <span className="text-lg text-gray-500"> /month</span>
                                            </p>

                                            {billing === 'yearly' && (
                                                <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                                            )}

                                            <ul className="space-y-3 my-8 text-left">
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Up to 3 children</li>
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Personalized learning paths</li>
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Structured STEM lessons</li>
                                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Shared parent dashboard</li>
                                            </ul>

                                            <button
                                                onClick={() => handleSubscribe(pricing.family[billing].price_id)}
                                                className="block w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition"
                                            >
                                                Get Family Plan
                                            </button>
                                        </div>

                                    </div>

                                    <p className="text-center text-sm text-gray-500 mt-6">
                                        Prices shown in {pricing.currency}. Taxes may apply.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {/* Add/Edit Student Modal */}
                {showAddModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-800">{editingStudent ? 'Edit Child Profile' : 'Add New Child'}</h2>
                                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Child's Name *</label>
                                    <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none" placeholder="Enter child's name" />
                                </div>

                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Date of Birth *</label>
                                    <input
                                        type="date"
                                        value={formData.dob}
                                        min={formatDate(minDob)}
                                        max={formatDate(maxDob)}
                                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none"
                                    />
                                    {/*<input type="date" value={formData.dob} onChange={(e) => setFormData({...formData, dob: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none" />*/}
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Activities Your Child Enjoys (Choose up to 3)</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {HOBBIES.map(hobby => {
                                            const selected = formData.hobbies.includes(hobby.id);
                                            return (
                                                <button
                                                    type="button"
                                                    key={hobby.id}
                                                    onClick={() => {
                                                        // toggle max 3
                                                        if (selected) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                hobbies: prev.hobbies.filter(h => h !== hobby.id)
                                                            }));
                                                        } else if (formData.hobbies.length < 3) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                hobbies: [...prev.hobbies, hobby.id]
                                                            }));
                                                        }
                                                    }}
                                                    className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                                        selected ? 'bg-purple-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {hobby.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Selected: {formData.hobbies.length}/3</p>
                                </div>

                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Choose Avatar</label>
                                    <div className="grid grid-cols-5 gap-2">
                                        {avatars.map(avatar => (
                                            <button key={avatar} type="button" onClick={() => setFormData({...formData, avatar})} className={`text-4xl p-3 rounded-xl transition-all ${formData.avatar === avatar ? 'bg-purple-200 scale-110 ring-2 ring-purple-500' : 'bg-gray-100 hover:bg-gray-200'}`}>
                                                {avatar}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all">Cancel</button>
                                    <button type="submit" className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all">{editingStudent ? 'Save Changes' : 'Add Child'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParentDashboard;
